/** Snap tolerance in px per sensitivity (parity with drawing MAGNET_TOLERANCE). */
export const CROSSHAIR_MAGNET_TOLERANCE_PX = {
    weak: 5,
    normal: 10,
    strong: 20,
};
/** Shift+drag on the Y axis zooms at this fraction of the normal rate (R-P2-01 fine zoom). */
export const Y_AXIS_FINE_ZOOM_RATE = 0.5;
function isFinitePrice(value) {
    return typeof value === "number" && Number.isFinite(value);
}
/** Extract finite OHLC from a type-erased enriched datum (engine data is Record<string, unknown>[]). */
export function readOhlc(datum) {
    if (datum === null || typeof datum !== "object")
        return null;
    const record = datum;
    const { open, high, low, close } = record;
    if (!isFinitePrice(open) || !isFinitePrice(high) || !isFinitePrice(low) || !isFinitePrice(close)) {
        return null;
    }
    return { open, high, low, close };
}
/**
 * Snap the crosshair Y to the nearest OHLC of the hovered bar.
 * Pure + total: never throws, never returns NaN, ties prefer close.
 */
export function snapCrosshairYToOHLC(input) {
    const safeY = Number.isFinite(input.cursorY) ? input.cursorY : 0;
    const fallback = { y: safeY, snappedPrice: null, snappedField: null };
    if (input.sensitivity === "off" || input.ohlc === null)
        return fallback;
    if (!Number.isFinite(input.cursorY))
        return fallback;
    const tolerance = CROSSHAIR_MAGNET_TOLERANCE_PX[input.sensitivity];
    // Evaluated worst-first so exact ties resolve to close (preferred anchor).
    const fields = ["open", "low", "high", "close"];
    let best = fallback;
    let bestDistance = tolerance;
    for (const field of fields) {
        const price = input.ohlc[field];
        const y = input.priceToY(price);
        if (y === null || !Number.isFinite(y))
            continue;
        const distance = Math.abs(y - input.cursorY);
        if (distance <= bestDistance) {
            bestDistance = distance;
            best = { y, snappedPrice: price, snappedField: field };
        }
    }
    return best;
}
/**
 * P2 (E1): Y-axis drag zoom core — shared by the canvas gesture path and
 * the public `zoomYAxis` API so both stay bit-identical.
 * Positive deltaY (drag down) expands the domain (zoom out), matching the
 * existing gesture math `ratio = exp((diffY / height) * 2)`.
 */
export function zoomDomainByPixels(domain, deltaYPx, heightPx, fine = false) {
    const [min, max] = domain;
    if (!Number.isFinite(min) || !Number.isFinite(max) || !(heightPx > 0) || !Number.isFinite(deltaYPx)) {
        return [min, max];
    }
    const rate = fine ? Y_AXIS_FINE_ZOOM_RATE : 1;
    const ratio = Math.exp(((deltaYPx / heightPx) * 2) * rate);
    if (!Number.isFinite(ratio) || ratio <= 0)
        return [min, max];
    const center = (min + max) / 2;
    const halfSpan = (max - min) / 2;
    return [center - halfSpan * ratio, center + halfSpan * ratio];
}
